/**
 * Géométrie de la scène orbitale.
 *
 * Ce module n'importe rien — un test lit le fichier et le vérifie. Il ne peut
 * donc ni intégrer une trajectoire, ni choisir un exposant : le chemin lui est
 * **donné**, en mètres, par l'appelant qui le tient du noyau. Ce qu'il décide,
 * ce sont des positions dans une scène.
 *
 * ## Ce que la scène montre
 *
 * Le plan de l'orbite est posé à plat et la caméra le regarde de biais : c'est
 * une scène, pas un diagramme vu de face. L'échelle est celle de la distance
 * de départ, qui vaut un — comme la hauteur de lâcher dans la chute. Les
 * distances dessinées sont donc dans le rapport exact des distances calculées.
 *
 * ## Le sillage se dévoile, il ne se reconstruit pas
 *
 * Le chemin complet est fourni d'un coup, avec la fraction déjà parcourue. La
 * vue peut ainsi construire la ligne une seule fois et n'en révéler qu'une
 * part croissante, au lieu de rebâtir deux mille points à chaque image. C'est
 * la même leçon que la chute libre, apprise une fois : ce qui bouge se déplace,
 * ce qui ne bouge pas ne se reconstruit pas.
 *
 * Hors du noyau : nombres nus et `Math` autorisés (les unités ici sont des
 * unités de scène, pas des grandeurs physiques).
 */

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface Point2 {
  readonly x: number;
  readonly y: number;
}

/** Ce que l'appelant doit fournir. Aucun de ces nombres n'est décidé ici. */
export interface OrbitSceneRequest {
  /** Distance de référence, en mètres : elle vaut un dans la scène. */
  readonly scale: number;
  readonly bodies: readonly OrbitBodyRequest[];
}

export interface OrbitBodyRequest {
  readonly id: string;
  readonly label: string;
  /** Le chemin complet, en mètres. Vide quand le modèle ne prédit rien. */
  readonly path: readonly Point2[];
  /** Fraction déjà parcourue, de 0 au départ à 1 à la fin du chemin. */
  readonly progress: number;
}

export interface OrbitBodySketch {
  readonly id: string;
  readonly label: string;
  /** Le chemin entier, en unités de scène. */
  readonly trail: readonly Vec3[];
  /** Nombre de points du sillage à montrer. Le reste n'a pas encore eu lieu. */
  readonly revealed: number;
  readonly centre: Vec3;
  readonly radius: number;
}

export interface OrbitScene {
  readonly centralRadius: number;
  readonly bodies: readonly OrbitBodySketch[];
  readonly bounds: { readonly min: Vec3; readonly max: Vec3 };
}

/** Distance de départ en unités de scène : toute la scène s'y rapporte. */
const REFERENCE = 1;
const CENTRAL_RADIUS = 0.075;
const BODY_RADIUS = 0.032;

function vec(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(high, value));
}

/**
 * Le plan orbital est posé à plat : le `y` du calcul devient le `z` de la
 * scène. Seule subsiste une hauteur infime, celle du décalage décrit ci-dessous.
 * Une orbite regardée de biais se lit comme une scène ; regardée de face, comme
 * un dessin sur une feuille.
 */
function toScene(point: Point2, scale: number, lift: number): Vec3 {
  return vec((point.x / scale) * REFERENCE, lift, (point.y / scale) * REFERENCE);
}

/**
 * Décalage vertical par modèle, minuscule et volontaire.
 *
 * Les sillages vivent tous dans le plan de l'orbite, donc exactement l'un sur
 * l'autre là où les trajectoires coïncident — au départ, elles coïncident
 * toutes. Sans ce décalage, la carte de profondeur départage les lignes au
 * hasard du pilote graphique, et celle qu'on voit n'est pas la même d'une
 * machine à l'autre. Un dix-millième de la distance de référence suffit : c'est
 * invisible à l'œil, et sans appel pour le rendu.
 */
const LIFT_PER_BODY = 0.0004;

export function buildOrbitScene(request: OrbitSceneRequest): OrbitScene {
  const scale = request.scale > 0 ? request.scale : 1;

  const bodies: OrbitBodySketch[] = [];
  request.bodies.forEach((body, rank) => {
    if (body.path.length < 2) return;

    // Le dernier modèle est posé le plus bas : les sillages tracés ensuite
    // passent au-dessus, si bien que celui de tête reste visible même quand un
    // autre le recouvre.
    const lift = (request.bodies.length - 1 - rank) * LIFT_PER_BODY;
    const trail = body.path.map((point) => toScene(point, scale, lift));
    // Au moins deux points : une ligne d'un seul point ne se dessine pas, et
    // le corps doit bien être quelque part dès la première image.
    const revealed = Math.max(2, Math.round(clamp(body.progress, 0, 1) * (trail.length - 1)) + 1);
    const centre = trail[revealed - 1] ?? trail[0];
    if (centre === undefined) return;

    bodies.push({
      id: body.id,
      label: body.label,
      trail,
      revealed,
      centre,
      radius: BODY_RADIUS,
    });
  });

  return { centralRadius: CENTRAL_RADIUS, bodies, bounds: boundsOf(bodies) };
}

function boundsOf(bodies: readonly OrbitBodySketch[]): {
  readonly min: Vec3;
  readonly max: Vec3;
} {
  // Le corps central occupe au moins son propre rayon : une orbite qui
  // s'effondrerait ne doit pas faire disparaître la scène.
  let extent = CENTRAL_RADIUS;
  for (const body of bodies) {
    for (const point of body.trail) {
      extent = Math.max(extent, Math.abs(point.x), Math.abs(point.z));
    }
  }
  return {
    min: vec(-extent, 0, -extent),
    max: vec(extent, 0, extent),
  };
}

/** Le centre de la scène : le corps central, toujours à l'origine. */
export function orbitCentre(): Vec3 {
  return vec(0, 0, 0);
}

/**
 * Les points **réellement occupés**, pour le cadrage.
 *
 * Le sillage entier, pas seulement la part révélée : sans quoi la caméra
 * s'écarterait au fur et à mesure que l'orbite se dessine, et le mouvement se
 * doublerait d'un zoom que personne n'a demandé. Le cadre doit être celui de
 * la trajectoire complète dès la première image.
 */
export function orbitScenePoints(scene: OrbitScene): readonly Vec3[] {
  const points: Vec3[] = [
    vec(scene.centralRadius, 0, 0),
    vec(-scene.centralRadius, 0, 0),
    vec(0, 0, scene.centralRadius),
    vec(0, 0, -scene.centralRadius),
  ];

  for (const body of scene.bodies) {
    for (const point of body.trail) points.push(point);
  }
  return points;
}

export const DRAWN_REFERENCE = REFERENCE;
export const DRAWN_CENTRAL_RADIUS = CENTRAL_RADIUS;
export const DRAWN_BODY_RADIUS = BODY_RADIUS;

/** Une mémoire par vue : les chemins immuables servent d'identité, sans signature partielle. */
export function createOrbitSceneBuilder(): (request: OrbitSceneRequest) => OrbitScene {
  let previous: OrbitSceneRequest | undefined;
  let prepared: OrbitScene | undefined;
  return (request) => {
    if (previous === undefined || prepared === undefined || previous.scale !== request.scale ||
      previous.bodies.length !== request.bodies.length || request.bodies.some((body, index) => {
        const old = previous?.bodies[index];
        return old?.id !== body.id || old?.label !== body.label || old?.path !== body.path;
      })) {
      prepared = buildOrbitScene(request);
      previous = request;
    }
    const progress = new Map(request.bodies.map((body) => [body.id, body.progress]));
    return {
      ...prepared,
      bodies: prepared.bodies.map((body) => {
        const revealed = Math.max(2, Math.round(clamp(progress.get(body.id) ?? 0, 0, 1) * (body.trail.length - 1)) + 1);
        return { ...body, revealed, centre: body.trail[revealed - 1] ?? body.centre };
      }),
    };
  };
}
